import {
  Bot,
  BriefcaseBusiness,
  CalendarDays,
  CirclePause,
  Home,
  Inbox,
  Link2,
  MessageCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  X,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useWorkspace } from "../lib/workspace";
import { Badge } from "./ui";

const navigation = [
  { to: "/", label: "Home", icon: Home },
  { to: "/content", label: "Content", icon: CalendarDays },
  { to: "/comments", label: "Comments", icon: MessageCircle },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/business-context", label: "Business Context", icon: BriefcaseBusiness },
  { to: "/automation", label: "Automation", icon: Bot },
  { to: "/connections", label: "Connections", icon: Link2 },
  { to: "/settings", label: "Settings", icon: Settings },
] as const;

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { status } = useAuth();
  const { data, error, notice, dismissNotice } = useWorkspace();
  const needsReview =
    (data?.inbox.filter((item) => item.reviewStatus === "needs_review").length ?? 0) +
    (data?.comments.filter((item) => item.status === "needs_review").length ?? 0);

  return (
    <div className={`app-shell ${collapsed ? "app-shell--collapsed" : ""}`}>
      <aside className="sidebar">
        <div className="sidebar__brand">
          <div className="brand-mark" aria-hidden="true">A</div>
          <div className="brand-copy">
            <strong>AutoSocial</strong>
            <span>DrTun workspace</span>
          </div>
          <button
            className="icon-button sidebar__collapse"
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="Primary navigation">
          {navigation.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
              title={collapsed ? label : undefined}
            >
              <Icon size={19} strokeWidth={1.8} aria-hidden="true" />
              <span>{label}</span>
              {label === "Inbox" && needsReview > 0 ? (
                <span className="nav-count" aria-label={`${needsReview} items need review`}>
                  {needsReview}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="security-chip">
            <ShieldCheck size={17} />
            <span>Human approval on</span>
          </div>
          <p>v0.1 · Facebook first</p>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div className="topbar__status">
            {status === "preview" ? <Badge tone="purple">Preview data</Badge> : <Badge tone="green">Secure session</Badge>}
            {data?.settings.sendsPaused ? (
              <span className="paused-chip"><CirclePause size={16} /> All sends paused</span>
            ) : (
              <span className="paused-chip">Human approval required</span>
            )}
          </div>
          <div className="topbar__workspace">
            <span>{data?.workspace.name ?? "Loading workspace…"}</span>
            <div className="avatar" aria-hidden="true">DT</div>
          </div>
        </header>

        {error ? (
          <div className="global-banner global-banner--error" role="alert">
            <span>{error}</span>
          </div>
        ) : null}
        {notice ? (
          <div className="global-banner" role="status">
            <span>{notice}</span>
            <button className="icon-button" type="button" onClick={dismissNotice} aria-label="Dismiss">
              <X size={17} />
            </button>
          </div>
        ) : null}

        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
